/**
 * DUPLICATE — the town-square copy is `town-square/src/components/featureWizard/FeatureWizardStep.tsx`.
 * Edit both or neither: the admin authoring preview and both overlay hosts must render an
 * identical step, and that identity is the whole point of this component. This cannot live in
 * `shared/` — `.tsx` there breaks the api/worker deploy build. See
 * `docs/decisions/0002-shared-react-components.md`.
 */
import type { ExplainerCtaConfig } from '@arthelper/shared/feature-wizard-config';

/**
 * The one presentational rendering of an EXPLAINER_CTA step, rendered by the admin authoring
 * preview and this app's wizard overlay host. A preview that re-implements the step is a
 * preview that lies the first time either side changes.
 *
 * It owns no eventing, no data fetching and no wizard state: it takes the authored config,
 * the position within the wizard, two handlers and two presentational flags, and calls the
 * handlers. What a click MEANS — advance, complete, navigate, or close a preview — is the
 * host's decision, so nothing here may branch on `position === total`.
 *
 * It renders the INNER CONTENT of a panel only. Every host owns its own dialog, backdrop,
 * portal, focus trap and close control, so there is deliberately no chrome here — and the
 * host's panel should carry no padding of its own, because the 20px here is the design's.
 */
export type FeatureWizardStepProps = {
  config: ExplainerCtaConfig;
  /** 1-based position of this step. */
  position: number;
  /** Total authored steps. A wizard has no maximum length, so this is a counter, not dots. */
  total: number;
  /**
   * The already-interpolated progress counter, exactly as `errorMessage` is already-resolved
   * copy: translation is a hook in both apps and cannot live in here. Omitted, it falls back to
   * English, which is what the admin authoring preview wants — that screen is English-only.
   */
  progressLabel?: string;
  /** A recording is in flight: both actions are disabled until the host clears it. */
  isBusy?: boolean;
  /** A failed recording the reader can retry by pressing the button again. Non-blocking. */
  errorMessage?: string | null;
  onPrimary: () => void;
};

export function FeatureWizardStep({
  config,
  position,
  total,
  progressLabel = `Step ${position} of ${total}`,
  isBusy = false,
  errorMessage = null,
  onPrimary,
}: FeatureWizardStepProps) {
  return (
    // The one stable handle on the overlay's contents. Every host supplies its own dialog
    // chrome, so a browser test has no shared selector for "the wizard is showing" without it.
    <div data-testid="feature-wizard-step" className="w-full max-w-[460px] p-5 text-left">
      <p className="flex min-h-6 items-center font-mono text-[11px] tracking-[0.03em] text-zinc-500">{progressLabel}</p>

      {/* No image means no element at all: a step without one renders exactly as a wizard
          that has no images anywhere does, with no reserved empty space. */}
      {config.imageUrl ? (
        <img
          src={config.imageUrl}
          alt={config.imageAlt ?? ''}
          className="mb-4 h-[120px] w-full rounded-lg bg-zinc-100 object-contain p-3"
        />
      ) : null}

      <h2 className="text-xl font-semibold leading-[1.3] tracking-[-0.02em] text-zinc-900">{config.headline}</h2>

      {/* Plain text, never markup: the authored body is copy, not rich content. */}
      <p className="mt-2.5 mb-[22px] text-[14.5px] leading-[1.55] text-zinc-600">{config.body}</p>

      {errorMessage ? (
        <p role="alert" className="mb-2.5 text-[13px] leading-snug text-red-600">
          {errorMessage}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onPrimary}
        disabled={isBusy}
        aria-busy={isBusy}
        className="min-h-[42px] w-full rounded-lg bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-900 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {config.ctaLabel}
      </button>
    </div>
  );
}
